import { assign, createMachine } from "xstate";
import router from "next/router";
import { pipe } from "fp-ts/lib/function";
import { array, option } from "fp-ts";
import {
  PROTOCOL_INSTANCE_PRIORITY,
  ProtocolInstance,
  getProtocolInstanceChainId,
  isRuntimeProtocolInstance,
  isRuntimeSupportedChainId,
} from "@silo/types";

type Events =
  | { type: "CHAIN_UPDATED"; chainId: number }
  | {
      type: "SET_PROTOCOL_INSTANCE";
      targetProtocolInstance: ProtocolInstance;
      redirectOnError: boolean;
    };

interface Context {
  chain?: number;
  protocolInstance?: ProtocolInstance;
  targetProtocolInstance?: ProtocolInstance;
  redirectOnError?: boolean;
}

export const createProtocolInstanceMachine = (
  persistedProtocolInstance: ProtocolInstance | undefined
) =>
  createMachine(
    {
      /** @xstate-layout N4IgpgJg5mDOIC5QAoC2BDAxgCwJYDswBKAYgGEAJAQQEkA5AfQFUAFAESoBUBRNgbQAMAXUSgADgHtYuAC64J+USAAeiALQBmAKwA6AGx6BAdgCMWkwCYTATgvWBJvQBoQAT0Qmj1-Ra3bDGkYCWgAsegC+4S5oWHiEpJS0jKwcPPwmIkggktJyCkqqCGphOgAcpUaleiYhpSECweUu7ghaVjoansZa9hoGIdaR0Rg4BMQ6AG7oADa4EGTY6AQ6c9NgJADK3JwMLABKAPKcB2QHADIM9BucVHRk3IKZ4lKy8opZhUYa3ta-daXfGwaEJaZoePSlHRaLSlSwCCGBGGlIYgGKjeKTGZzBZLfArCBrTbbXaHY6nC5XG53B4ZJQ5V75D6IEI1HQhUzGX5GIKVMEICylXRhbl6DR2LRGCxGCJRVEjOLjKazeaLZboGQyMCoMRyfBQHEEJhiCDq9ZgABO5ok5p0Ymm6oAZtbUDo0QqiJjlQa8erNdrdfrVfgjSbNQgCBMJJh1W9Ho86S88u9QIV7HodDUTAIWSENAJtBZnG4PCEM6UBdojCEwlVgSEUW6xh6I1iVbj8YStjt9kcTudLnRrrd7vGsvSkwVEEZoVCBtoxX0vtYQny9NYNDp4dz4Q0LGLfg35U2VvgldigzpfVqdQRA7iQ6aSBarTa7Y7na6jxiW16L1f-be3oPmGEZRjGChxsICa5G8k4IPY3gaMCei5gIFjZmupR8m0JhlAhIJIfmpTZpEsr4BIEBwEojbxNBDLJio6hWJClQCqYFQWPUwRFi0aiOAIOiVIKJgVKY1TVPWso0eMYhgPgEC3nRE5MkUXSCeWFQiZKXFaDxiCdGy1hZg0xHlm0AjWDKwyxMeZ5tgQSmwSpfTpuyWZeNY268sWRRfB0aEAgC5jESyRiHjZGJ2d6HZgI5jIpsy6FlDUaHsoYEomHyJh9Ju0rsjCNSSuYknWeiiqttF-43nqQHGqacUMYUJg1BYZTpcYFiFtC5ZZWKm6itYVR2GYhgieFZXNqeFVBg1cGBLhxhmMxIRShCmU+bmpb1G0QSdeYhXje6J5RReqyxWOiZOQlCAst4i04XUq2wqu5ZQnUnmeD0jjVIdx4-ue7ZVQGtWhudzwwfFjEIM1K1tcEHVdTCFirt8HRSp1EJrfmWikeEQA */
      predictableActionArguments: true,
      // https://xstate.js.org/docs/guides/context.html#action-order
      preserveActionOrder: true,
      tsTypes: {} as import("./machine.typegen").Typegen0,
      schema: {
        events: {} as Events,
        context: {} as Context,
        services: {} as {
          switchChain: { data: void };
        },
      },
      on: {
        CHAIN_UPDATED: [
          {
            cond: "isRuntimeSupportedChain",
            target: "validChain",
            actions: ["setChain", "setProtocolInstanceFromChain"],
          },
          {
            target: "invalidChain",
            actions: "setChain",
          },
        ],
      },
      initial: "pending",
      states: {
        pending: {
          entry: "targetPersistedProtocolInstance",
        },
        /**
         * IMPORTANT: The `validChain` and `invalidChain` state have a similar
         * config. This could probably be extracted into a separate machine that
         * both could use. But it also introduces additional complexity, so
         * kicking the can for now.
         */
        validChain: {
          initial: "idle",
          states: {
            idle: {
              entry: ["resetTargetProtocolInstance"],
              on: {
                SET_PROTOCOL_INSTANCE: [
                  {
                    cond: "isNewChain",
                    target: "attemptingChainUpdate",
                  },
                  {
                    cond: "isRuntimeProtocolInstance",
                    actions: "setProtocolInstance",
                  },
                ],
              },
            },
            attemptingChainUpdate: {
              tags: "attemptingChainUpdate",
              entry: "setAttemptContextValues",
              invoke: {
                src: "switchChain",
                onError: {
                  target: "idle",
                  actions: ["maybeRedirect", "resetRedirectOnError"],
                },
              },
            },
          },
        },
        invalidChain: {
          initial: "idle",
          states: {
            idle: {
              entry: ["resetTargetProtocolInstance"],
              on: {
                SET_PROTOCOL_INSTANCE: [
                  {
                    cond: "isNewChain",
                    target: "attemptingChainUpdate",
                  },
                ],
              },
            },
            attemptingChainUpdate: {
              tags: "attemptingChainUpdate",
              entry: "setAttemptContextValues",
              invoke: {
                src: "switchChain",
                onError: {
                  target: "idle",
                  actions: ["maybeRedirect", "resetRedirectOnError"],
                },
              },
            },
          },
        },
      },
    },
    {
      actions: {
        setChain: assign({
          chain: (_, event) => event.chainId,
        }),
        setProtocolInstance: assign({
          protocolInstance: (_, event) => event.targetProtocolInstance,
        }),
        setProtocolInstanceFromChain: assign((context, event) => {
          if (!isRuntimeSupportedChainId(event.chainId)) return context;

          const { targetProtocolInstance } = context;

          /**
           * If a target protocol instance has been set and it matches the new
           * chain id, set the protocol instance to it and reset the target.
           */
          if (
            isRuntimeProtocolInstance(targetProtocolInstance) &&
            getProtocolInstanceChainId(targetProtocolInstance) === event.chainId
          ) {
            return {
              ...context,
              protocolInstance: targetProtocolInstance,
              targetProtocolInstance: undefined,
            };
          }

          /**
           * Otherwise, set the protocol instance to the highest priority value
           * for the new chain.
           */
          const priorityProtocolInstance = pipe(
            PROTOCOL_INSTANCE_PRIORITY[event.chainId],
            array.filter(isRuntimeProtocolInstance),
            array.head,
            option.toUndefined
          );

          return {
            ...context,
            protocolInstance: priorityProtocolInstance,
            targetProtocolInstance: undefined,
          };
        }),
        targetPersistedProtocolInstance: assign({
          targetProtocolInstance: () => persistedProtocolInstance,
        }),
        setAttemptContextValues: assign({
          targetProtocolInstance: (_, event) => event.targetProtocolInstance,
          redirectOnError: (_, event) => event.redirectOnError,
        }),
        maybeRedirect: (context) => {
          if (context.redirectOnError) {
            router.push("/");
          }
        },
        resetRedirectOnError: assign({
          redirectOnError: false,
        }),
        resetTargetProtocolInstance: assign({
          targetProtocolInstance: undefined,
        }),
      },
      guards: {
        isRuntimeSupportedChain: (_, event) =>
          isRuntimeSupportedChainId(event.chainId),
        isNewChain: (context, event) => {
          const targetChain = getProtocolInstanceChainId(
            event.targetProtocolInstance
          );

          return context.chain !== targetChain;
        },
        isRuntimeProtocolInstance: (_, event) =>
          isRuntimeProtocolInstance(event.targetProtocolInstance),
      },
    }
  );
